// Что записать в базу при переносе, растяжении, бэклоге и дублировании. Чистые функции:
// на вход задача, намерение и задачи её строки; на выход — список изменений для
// apply_changes с expect_updated_at по всем прочитанным строкам. Поведение — как
// в старом Projects: перенос на занятый трек ищет свободный, растяжение вправо расталкивает.
import { addDays, daysBetween, overlaps, type ISODate } from '@core/dates';
import type { Change } from '@core/store/model';
import type { Row } from '@core/store/model';

export type Task = Row<'task'>;
export type Assignee = Row<'task_assignee'>;

type RowKey = { project_id: string; subproject_id: string | null };

const sameRow = (t: Pick<Task, 'project_id' | 'subproject_id'>, key: RowKey): boolean =>
  t.project_id === key.project_id && (t.subproject_id ?? null) === (key.subproject_id ?? null);

const active = (t: Task) => !t.in_backlog;

/** Позиция для новой строки в конец списка — у структурных таблиц (лонч, подпроекты). */
export const nextPosition = (rows: Iterable<{ position: number }>): number => Math.max(-1, ...[...rows].map((r) => r.position)) + 1;

/** Ближайший свободный трек строки для отрезка дат. */
export function findFreeTrack(rowTasks: Task[], start: ISODate, end: ISODate, excludeId?: string): number {
  const others = rowTasks.filter((t) => active(t) && t.id !== excludeId);
  for (let track = 0; ; track++) {
    const busy = others.some((t) => (t.track ?? 0) === track && overlaps(t.start_date, t.end_date, start, end));
    if (!busy) return track;
  }
}

/**
 * Трек под курсором при переносе: слот экрана → трек (за последним — новый), занятый
 * отрезком чужой задачи → ближайший свободный.
 */
export function dropTrack(slots: number[], slotIndex: number, rowTasks: Task[], start: ISODate, end: ISODate, selfId: string): number {
  const wanted = slots[slotIndex] ?? (slots.length ? Math.max(...slots) + 1 : 0);
  const busy = rowTasks.some((t) => t.id !== selfId && active(t) && (t.track ?? 0) === wanted && overlaps(t.start_date, t.end_date, start, end));
  return busy ? findFreeTrack(rowTasks, start, end, selfId) : wanted;
}

type MoveTarget = RowKey & { start_date: ISODate; track: number };

/** Перенос: те же даты со сдвигом, новая строка и трек; занятый трек → свободный. Шаг — дело экрана. */
export function planMove(task: Task, target: MoveTarget, targetRowTasks: Task[]): Change[] {
  const start = target.start_date;
  const delta = daysBetween(task.start_date, start);
  const end = addDays(task.end_date, delta);
  const others = targetRowTasks.filter((t) => active(t) && t.id !== task.id);
  const busy = others.some((t) => (t.track ?? 0) === target.track && overlaps(t.start_date, t.end_date, start, end));
  const track = busy ? findFreeTrack(targetRowTasks, start, end, task.id) : target.track;
  const unchanged =
    start === task.start_date && sameRow(task, target) && track === (task.track ?? 0) && !task.in_backlog;
  if (unchanged) return [];
  return [{
    table: 'task', id: task.id, expect_updated_at: task.updated_at,
    set: {
      start_date: start, end_date: end, track, in_backlog: false,
      project_id: target.project_id, subproject_id: target.subproject_id,
    },
  }];
}

/**
 * Растяжение до точной даты; задача не короче дня. Правый край: наезд на соседа по треку
 * расталкивает его и всех правее. Левый край: только сама задача, не левее конца левого соседа.
 */
export function planResize(task: Task, side: 'start' | 'end', newDate: ISODate, rowTasks: Task[]): Change[] {
  const track = task.track ?? 0;
  const neighbours = rowTasks.filter((t) => active(t) && t.id !== task.id && (t.track ?? 0) === track);

  if (side === 'end') {
    const end = newDate < task.start_date ? task.start_date : newDate;
    if (end === task.end_date) return [];
    const changes: Change[] = [{
      table: 'task', id: task.id, expect_updated_at: task.updated_at,
      set: { end_date: end },
    }];
    const delta = daysBetween(task.end_date, end);
    if (delta > 0) {
      const right = neighbours.filter((t) => t.start_date > task.end_date).sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
      const blocking = right.some((t) => t.start_date <= end);
      if (blocking) {
        for (const t of right) {
          changes.push({
            table: 'task', id: t.id, expect_updated_at: t.updated_at,
            set: { start_date: addDays(t.start_date, delta), end_date: addDays(t.end_date, delta) },
          });
        }
      }
    }
    return changes;
  }

  let start = newDate > task.end_date ? task.end_date : newDate;
  const left = neighbours.filter((t) => t.end_date < task.start_date).sort((a, b) => (a.end_date > b.end_date ? -1 : 1))[0];
  if (left) {
    const floor = addDays(left.end_date, 1);
    if (start < floor) start = floor;
  }
  if (start === task.start_date) return [];
  return [{ table: 'task', id: task.id, expect_updated_at: task.updated_at, set: { start_date: start } }];
}

/**
 * Групповой сдвиг по времени: все задачи на deltaDays, строка у каждой своя, трек — свой, если
 * свободен от задач вне группы и от уже расставленных членов группы (порядок — исходный трек,
 * потом дата начала). Вертикаль группы не трогаем: перенос между строками — только одиночный.
 */
type RowOf = { project_id: string | null; subproject_id: string | null };

export function planGroupMove(tasks: Task[], deltaDays: number, rowTasksOf: (key: RowOf) => Task[]): Change[] {
  if (deltaDays === 0) return [];
  const ids = new Set(tasks.map((t) => t.id));
  const byRow = new Map<string, Task[]>();
  for (const t of tasks) {
    const key = `${t.project_id}:${t.subproject_id ?? ''}`;
    byRow.set(key, [...(byRow.get(key) ?? []), t]);
  }
  const changes: Change[] = [];
  for (const group of byRow.values()) {
    const first = group[0]!;
    const placed = rowTasksOf({ project_id: first.project_id, subproject_id: first.subproject_id }).filter((t) => active(t) && !ids.has(t.id));
    const ordered = [...group].sort((a, b) => (a.track ?? 0) - (b.track ?? 0) || (a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0));
    for (const t of ordered) {
      const start = addDays(t.start_date, deltaDays);
      const end = addDays(t.end_date, deltaDays);
      const own = t.track ?? 0;
      const busy = placed.some((o) => (o.track ?? 0) === own && overlaps(o.start_date, o.end_date, start, end));
      const track = busy ? findFreeTrack(placed, start, end) : own;
      placed.push({ ...t, start_date: start, end_date: end, track });
      changes.push({ table: 'task', id: t.id, expect_updated_at: t.updated_at, set: { start_date: start, end_date: end, track } });
    }
  }
  return changes;
}

export function planBacklog(task: Task, inBacklog: boolean): Change[] {
  if (task.in_backlog === inBacklog) return [];
  return [{ table: 'task', id: task.id, expect_updated_at: task.updated_at, set: { in_backlog: inBacklog } }];
}

/** Копия рядом: тот же проект, «(copy)», свободный трек, те же исполнители. Id генерирует клиент. */
export function planDuplicate(task: Task, assignees: Assignee[], rowTasks: Task[], newId: string): Change[] {
  const track = task.in_backlog ? 0 : findFreeTrack(rowTasks, task.start_date, task.end_date);
  const {
    id: _id, created_at: _c, updated_at: _u, created_by: _cb, updated_by: _ub, legacy_id: _l, ...rest
  } = task;
  const changes: Change[] = [
    { table: 'task', insert: { ...rest, id: newId, title: `${task.title} (copy)`, track } },
  ];
  assignees
    .filter((a) => a.task_id === task.id)
    .forEach((a) => changes.push({ table: 'task_assignee', insert: { task_id: newId, person_id: a.person_id, position: a.position } }));
  return changes;
}

/** Копии нескольких задач: каждая следующая видит копии, уже поставленные в её строку. */
export function planDuplicateMany(tasks: Task[], assignees: Assignee[], rowTasksOf: (key: RowOf) => Task[], newIds: string[]): Change[] {
  const extra = new Map<string, Task[]>();
  const changes: Change[] = [];
  tasks.forEach((t, i) => {
    const key = `${t.project_id}:${t.subproject_id ?? ''}`;
    const rowTasks = [...rowTasksOf({ project_id: t.project_id, subproject_id: t.subproject_id }), ...(extra.get(key) ?? [])];
    const c = planDuplicate(t, assignees, rowTasks, newIds[i]!);
    const inserted = c[0] && 'insert' in c[0] ? (c[0].insert as Task) : null;
    if (inserted) extra.set(key, [...(extra.get(key) ?? []), inserted]);
    changes.push(...c);
  });
  return changes;
}

